import React from 'react';
import PropTypes from 'prop-types';
import SearchChipsCarousel from './SearchChipsCarousel';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/SearchBar/SearchChipsCarousel',
  component: SearchChipsCarousel,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `SearchChipsCarousel` component provides a horizontally scrollable list of chips that represent active search filters. It allows users to easily view and remove active filters.\n\n',
      // When & how to use it?
      '\nUse the `SearchChipsCarousel` component when displaying active search filters in a compact, scrollable format is required. It is commonly used alongside search bars to enhance the filtering experience.\n\n',
      // Current Usage
      '\nThe `SearchChipsCarousel` component is used on pages like Notes, Visits, and Routes to display active filters in a horizontal list format. It integrates with other search components to provide a seamless user experience.\n\n',
      // Related Components
      `\n- **[Search](http://localhost:6006/?path=/docs/components-searchbar-search--docs)**: Parent component that uses the \`SearchChipsCarousel\`.
       \n- **[Icon](http://localhost:6006/?path=/docs/components-icons-icon--docs)**: Provides icons for the chips and carousel arrows.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    chips: {
      control: { type: 'array' },
      description: 'Array of chips representing active filters. Each chip includes a `key` and `value`.\n\n',
      table: {
        type: { summary: 'array' },
        required: true,
      },
    },
    onClickCloseChip: {
      control: { type: 'function' },
      description: 'Callback function triggered when a chip is removed.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    chipsSettings: {
      control: { type: 'object' },
      description: 'Settings for configuring the chip labels, such as prefixes and types.\n\n',
      table: {
        type: { summary: 'object' },
        required: false,
      },
    },
  },
};

const DEFAULT_CHIPS = [];
const DEFAULT_CHIPS_SETTINGS = {};

const SearchChipsCarouselTemplate = ({
  chips = DEFAULT_CHIPS,
  onClickCloseChip = () => {},
  chipsSettings = DEFAULT_CHIPS_SETTINGS,
}) => {
  return (
    <div style={{
      minWidth: '400px', minHeight: '200px', border: '1px solid #ddd', padding: '10px',
    }}
    >
      <SearchChipsCarousel
        chips={chips}
        onClickCloseChip={onClickCloseChip}
        chipsSettings={chipsSettings}
      />
      <div
        id="chipMessage"
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#F8F8F8',
          color: '#000',
          padding: '5px 10px',
          borderRadius: '5px',
          fontSize: '14px',
          display: 'inline-block',
          marginBottom: '2px',
        }}
      />
    </div>
  );
};

const Template = SearchChipsCarouselTemplate.bind({});

export const DefaultChipsCarousel = Template.bind({});
DefaultChipsCarousel.args = {
  chips: [
    { key: 'keyword', value: 'React' },
    { key: 'dateFrom', value: '2024-01-01' },
    { key: 'dateTo', value: '2024-12-31' },
  ],
  onClickCloseChip: (e, key) => {
    document.getElementById('chipMessage').textContent = `Chip with key "${key}" removed!`;
    setTimeout(() => {
      document.getElementById('chipMessage').textContent = '';
    }, 3000);
  },
  chipsSettings: {
    keyword: { prefix: 'Keyword', type: 'text' },
    dateFrom: { prefix: 'From', type: 'date' },
    dateTo: { prefix: 'To', type: 'date' },
  },
};
DefaultChipsCarousel.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to display a horizontal carousel of chips representing active filters. Chips can be removed by clicking the close button.\n\n',
  // Where is it currently used on the product?
  '\nThe `SearchChipsCarousel` component is used in the `Search` component on pages like Notes, Visits, and Routes.\n\n',
);

SearchChipsCarouselTemplate.propTypes = {
  chips: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.string,
  })),
  onClickCloseChip: PropTypes.func,
  chipsSettings: PropTypes.objectOf(PropTypes.shape({
    prefix: PropTypes.string,
    type: PropTypes.string,
  })),
};
